import { PERMISSIONS } from "modules/PermissionsConstants";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useGetMakeList from "../hooks/useGetMake";
import { IMake } from "../types";
import Timer from "components/Timer";
import { toast } from "components/toastNotification";
import { useHistory, useLocation } from "react-router-dom";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import useDeleteMake from "../hooks/useDeleteMake";
import {
  addStateMake,
  editStateMake,
  makeFetching,
  makeFilterText,
  rowEditMake,
  selectedRowsStateMake,
} from "../atoms/atoms";

import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import EditButton from "components/EditButton";
import MakeForm from "../form/MakeForm";
import TableFilters from "components/TableFilters";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import AddButton from "components/AddButton";
import MaterialTableTS from "components/tables/MaterialTableTS";

export const MakeTable = ({}) => {
  const { data: directData, refetch: reFetch } = useGetMakeList();

  const BulkDelete = useDeleteMake();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateMake);
  const [selectedRows, setSelectedRows] = useRecoilState(selectedRowsStateMake);
  const [actionEditState, setActionEditState] = useRecoilState(editStateMake);
  const [filterTextState, setFilterTextState] = useRecoilState(makeFilterText);
  const setEditRowData = useSetRecoilState(rowEditMake);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(makeFetching);

  //Local States
  const [tableData, setTableData] = useState<IMake[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<IMake[]>([]);
  const [displayNote, setDisplayNote] = useState(false);
  const [edit, setEdit] = useState(false);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_leasedequipment_make_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_leasedequipment_make_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_leasedequipment_make_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  // Get Data
  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IMake[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].makeName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.makeName &&
        item.makeName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.makeId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Make/s" : "Make"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Make Name",
      field: "makeName",
      customSort: (a, b) =>
        a.makeName && b.makeName
          ? a.makeName.trim().localeCompare(b.makeName.trim())
          : a.makeName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <MakeForm reFetch={reFetch} />
        </div>
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              recentText={recent}
              setRecent={setRecent}
              textInputName={"MakeName"}
              columnDetails={"Make Name"}
              filterText={setFilterTextState}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div>
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Make"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Make"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default MakeTable;
